import React, { useEffect, useState } from 'react';
import { ArrowLeft, Building2, CheckCircle, Database, Eye, EyeOff, Info, Key, Save, Server, Settings } from 'lucide-react';

const DEFAULT_CONFIG = {
  enabled: true,
  environment: 'demo',
  baseUrl: 'https://api.mews-demo.com',
  client: 'Lodgeic/0.1-demo',
  clientTokenSecretName: '',
  clientTokenSecretValue: '',
  accessTokenSecretName: '',
  accessTokenSecretValue: '',
  enterpriseId: '',
  serviceIdsText: '',
  historicalDays: 31,
  forwardDays: 365
};
const MAX_HISTORICAL_DAYS = 365;
const MAX_FORWARD_DAYS = 730;

const baseUrlForEnvironment = (environment, currentBaseUrl) => {
  if (environment === 'demo') return 'https://api.mews-demo.com';
  if (environment === 'production') return 'https://api.mews.com';
  return currentBaseUrl || '';
};

const MewsConfig = ({ selectedLocation, onSave, onBack, isLoading, error, initialConfig = null }) => {
  const [mewsConfig, setMewsConfig] = useState(DEFAULT_CONFIG);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSecrets, setShowSecrets] = useState({
    clientToken: false,
    accessToken: false
  });

  useEffect(() => {
    if (!initialConfig?.mewsConfig) return;
    const existing = initialConfig.mewsConfig;
    const environment = existing.baseUrl === 'https://api.mews.com'
      ? 'production'
      : 'demo';

    setMewsConfig({
      ...DEFAULT_CONFIG,
      ...existing,
      environment,
      serviceIdsText: Array.isArray(existing.serviceIds) ? existing.serviceIds.join('\n') : (existing.serviceIds || '')
    });
  }, [initialConfig]);

  const updateField = (field, value) => {
    setMewsConfig(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'environment') {
        next.baseUrl = baseUrlForEnvironment(value, prev.baseUrl);
      }
      return next;
    });
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateField = (field, value) => {
    let fieldError = null;
    const trimmed = typeof value === 'string' ? value.trim() : value;

    switch (field) {
      case 'baseUrl':
        if (!trimmed) fieldError = 'Base URL is required';
        else if (!/^https:\/\/api\.mews(-demo)?\.com$/.test(trimmed)) {
          fieldError = 'Use the selected MEWS environment URL';
        }
        break;
      case 'client':
        if (!trimmed) fieldError = 'Client name is required';
        break;
      case 'clientTokenSecretName':
        if (!trimmed) fieldError = 'Client token secret name is required';
        break;
      case 'accessTokenSecretName':
        if (!trimmed) fieldError = 'Access token secret name is required';
        break;
      case 'historicalDays':
        if (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > MAX_HISTORICAL_DAYS) {
          fieldError = `Historical days must be 1-${MAX_HISTORICAL_DAYS}`;
        }
        break;
      case 'forwardDays':
        if (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > MAX_FORWARD_DAYS) {
          fieldError = `Forward days must be 1-${MAX_FORWARD_DAYS}`;
        }
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({ ...prev, [field]: fieldError }));
    return fieldError;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, mewsConfig[field]);
  };

  const validateAll = () => {
    const fields = ['baseUrl', 'client', 'clientTokenSecretName', 'accessTokenSecretName', 'historicalDays', 'forwardDays'];
    const errors = fields.reduce((acc, field) => ({ ...acc, [field]: validateField(field, mewsConfig[field]) }), {});
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return Object.values(errors).every(value => !value);
  };

  const serviceIds = mewsConfig.serviceIdsText
    .split(/[\n,]/)
    .map(value => value.trim())
    .filter(Boolean);

  const handleSave = () => {
    if (!validateAll()) return;
    onSave({
      enabled: mewsConfig.enabled,
      baseUrl: mewsConfig.baseUrl.trim(),
      client: mewsConfig.client.trim(),
      clientTokenSecretName: mewsConfig.clientTokenSecretName.trim(),
      accessTokenSecretName: mewsConfig.accessTokenSecretName.trim(),
      ...(mewsConfig.clientTokenSecretValue.trim() ? { clientTokenSecretValue: mewsConfig.clientTokenSecretValue.trim() } : {}),
      ...(mewsConfig.accessTokenSecretValue.trim() ? { accessTokenSecretValue: mewsConfig.accessTokenSecretValue.trim() } : {}),
      enterpriseId: mewsConfig.enterpriseId.trim() || null,
      serviceIds,
      historicalDays: Number(mewsConfig.historicalDays),
      forwardDays: Number(mewsConfig.forwardDays)
    });
  };

  const isFormValid = mewsConfig.baseUrl.trim()
    && mewsConfig.client.trim()
    && mewsConfig.clientTokenSecretName.trim()
    && mewsConfig.accessTokenSecretName.trim()
    && !Object.values(validationErrors).some(Boolean);

  const inputClass = (field) => `cp-input${touched[field] && validationErrors[field] ? ' cp-invalid' : ''}`;

  return (
    <div className="ep-config-form">
      <div className="flex items-center space-x-2">
        <Database className="w-5 h-5 text-blue-600" />
        <h2 className="ep-config-title">MEWS PMS Integration</h2>
      </div>

      {selectedLocation && (
        <div className="ep-panel">
          <p className="cp-help">
            Configuring MEWS integration for: <strong>{selectedLocation.name}</strong> ({selectedLocation.code})
          </p>
        </div>
      )}

      <div className="ep-banner ep-tone-info">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ep-banner-body">
            <p className="font-medium mb-1">Key Vault based credentials</p>
            <p>Enter the Key Vault secret names for the MEWS client token and property access token. Token values are not stored in Cosmos.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="ep-banner ep-tone-error">
          <p className="ep-banner-body">{error}</p>
        </div>
      )}

      <div className="ep-config-fields">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={mewsConfig.enabled}
            onChange={(event) => updateField('enabled', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Enable MEWS sync</span>
            <p className="cp-help">Disabled configs stay saved but will not be used by the ETL.</p>
          </div>
        </label>

        <div>
          <label className="cp-label">
            <Server className="inline-block w-4 h-4 mr-1" />
            Environment
          </label>
          <select
            value={mewsConfig.environment}
            onChange={(event) => updateField('environment', event.target.value)}
            className="cp-input"
          >
            <option value="demo">Demo</option>
            <option value="production">Production</option>
          </select>
        </div>

        <div>
          <label className="cp-label">Base URL</label>
          <input
            type="text"
            value={mewsConfig.baseUrl}
            onChange={(event) => updateField('baseUrl', event.target.value)}
            onBlur={() => handleBlur('baseUrl')}
            disabled={mewsConfig.environment !== 'custom'}
            className={`${inputClass('baseUrl')} disabled:bg-gray-100 dark:disabled:bg-gray-800`}
          />
          {touched.baseUrl && validationErrors.baseUrl && <p className="cp-help cp-error">{validationErrors.baseUrl}</p>}
        </div>

        <div>
          <label className="cp-label">
            <Settings className="inline-block w-4 h-4 mr-1" />
            Client Name <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={mewsConfig.client}
            onChange={(event) => updateField('client', event.target.value)}
            onBlur={() => handleBlur('client')}
            placeholder="Lodgeic/0.1-demo"
            className={inputClass('client')}
          />
          {touched.client && validationErrors.client && <p className="cp-help cp-error">{validationErrors.client}</p>}
        </div>

        <div className="ep-grid">
          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Client Token Secret <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={mewsConfig.clientTokenSecretName}
              onChange={(event) => updateField('clientTokenSecretName', event.target.value)}
              onBlur={() => handleBlur('clientTokenSecretName')}
              placeholder="mews-demo-client-token"
              className={inputClass('clientTokenSecretName')}
            />
            {touched.clientTokenSecretName && validationErrors.clientTokenSecretName && (
              <p className="cp-help cp-error">{validationErrors.clientTokenSecretName}</p>
            )}
            <div className="relative mt-2">
              <input
                type={showSecrets.clientToken ? 'text' : 'password'}
                value={mewsConfig.clientTokenSecretValue}
                onChange={(event) => updateField('clientTokenSecretValue', event.target.value)}
                placeholder="Optional. Paste token to create/update this Key Vault secret."
                autoComplete="new-password"
                className={`${inputClass('clientTokenSecretValue')} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowSecrets(prev => ({ ...prev, clientToken: !prev.clientToken }))}
                className="ep-reveal"
                aria-label={showSecrets.clientToken ? 'Hide client token' : 'Show client token'}
                title={showSecrets.clientToken ? 'Hide client token' : 'Show client token'}
              >
                {showSecrets.clientToken ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Access Token Secret <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={mewsConfig.accessTokenSecretName}
              onChange={(event) => updateField('accessTokenSecretName', event.target.value)}
              onBlur={() => handleBlur('accessTokenSecretName')}
              placeholder="mews-location-93-access-token"
              className={inputClass('accessTokenSecretName')}
            />
            {touched.accessTokenSecretName && validationErrors.accessTokenSecretName && (
              <p className="cp-help cp-error">{validationErrors.accessTokenSecretName}</p>
            )}
            <div className="relative mt-2">
              <input
                type={showSecrets.accessToken ? 'text' : 'password'}
                value={mewsConfig.accessTokenSecretValue}
                onChange={(event) => updateField('accessTokenSecretValue', event.target.value)}
                placeholder="Optional. Paste token to create/update this Key Vault secret."
                autoComplete="new-password"
                className={`${inputClass('accessTokenSecretValue')} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowSecrets(prev => ({ ...prev, accessToken: !prev.accessToken }))}
                className="ep-reveal"
                aria-label={showSecrets.accessToken ? 'Hide access token' : 'Show access token'}
                title={showSecrets.accessToken ? 'Hide access token' : 'Show access token'}
              >
                {showSecrets.accessToken ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="cp-label">
            <Building2 className="inline-block w-4 h-4 mr-1" />
            Enterprise ID
          </label>
          <input
            type="text"
            value={mewsConfig.enterpriseId}
            onChange={(event) => updateField('enterpriseId', event.target.value)}
            placeholder="Optional for single-property access tokens"
            className={inputClass('enterpriseId')}
          />
        </div>

        <div>
          <label className="cp-label">Service IDs</label>
          <textarea
            value={mewsConfig.serviceIdsText}
            onChange={(event) => updateField('serviceIdsText', event.target.value)}
            placeholder="Optional. One bookable service ID per line."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
          />
          <p className="cp-help">Leave blank to let MEWS service discovery choose bookable services.</p>
        </div>

        <div className="ep-grid">
          <div>
            <label className="cp-label">Historical Days</label>
            <input
              type="number"
              min="1"
              max={MAX_HISTORICAL_DAYS}
              value={mewsConfig.historicalDays}
              onChange={(event) => updateField('historicalDays', event.target.value)}
              onBlur={() => handleBlur('historicalDays')}
              className={inputClass('historicalDays')}
            />
            {touched.historicalDays && validationErrors.historicalDays && <p className="cp-help cp-error">{validationErrors.historicalDays}</p>}
          </div>

          <div>
            <label className="cp-label">Forward Days</label>
            <input
              type="number"
              min="1"
              max="730"
              value={mewsConfig.forwardDays}
              onChange={(event) => updateField('forwardDays', event.target.value)}
              onBlur={() => handleBlur('forwardDays')}
              className={inputClass('forwardDays')}
            />
            {touched.forwardDays && validationErrors.forwardDays && <p className="cp-help cp-error">{validationErrors.forwardDays}</p>}
          </div>
        </div>
      </div>

      {isFormValid && (
        <div className="ep-banner ep-tone-ok">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="ep-banner-body">Configuration is valid and ready to save</p>
          </div>
        </div>
      )}

      <div className="ep-config-foot">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="cp-btn"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Setup
        </button>

        <button
          onClick={handleSave}
          disabled={isLoading || !isFormValid}
          className="cp-btn cp-primary"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MewsConfig;
